import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { Award } from '../../models/award.school.model';
import * as awardActions from '../actions/award.school.actions';

export interface AwardState extends EntityState<Award> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  awardStatsLoading: boolean;
  hasMore: boolean;
  error: AppExceptionModel | undefined;
}

export const awardAdapter: EntityAdapter<Award> = createEntityAdapter<Award>();

export const initialState: AwardState = awardAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  awardStatsLoading: false,
  hasMore: true,
  error: undefined,
});

export const schoolAwardReducer = createReducer(
  initialState,
  on(awardActions.getAwards, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(awardActions.getAward, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(awardActions.addAwardToStore, (state, { award }) =>
    awardAdapter.upsertOne(award, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(awardActions.addAwardsToStore, (state, { awards, hasMore }) => {
    return awardAdapter.upsertMany(awards, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      hasMore: hasMore !== undefined ? hasMore : awards.length >= 10,
      error: undefined,
    });
  }),
  on(awardActions.getAwardsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    hasMore: false,
    error: error,
  })),
  on(awardActions.getAwardFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(awardActions.create, (state, { award }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(awardActions.createSuccess, (state, { award }) =>
    awardAdapter.upsertOne(award, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(awardActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(awardActions.update, (state, { award }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(awardActions.updateSuccess, (state, { award }) =>
    awardAdapter.upsertOne(award, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(awardActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(awardActions.updateNotesSuccess, (state, { award }) =>
    awardAdapter.upsertOne(award, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(awardActions.updateNotesFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(awardActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(awardActions.deleteSuccess, (state, { id }) =>
    awardAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(awardActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(awardActions.clearFromStore, (state, { ids }) =>
    awardAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(awardActions.clear, (state) => initialState),
);
